import React from 'react';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

/**
 * Generic render functions for different field types
 * These can be referenced by x-render-type in the database schema
 */

export function renderDateNatural(value: any, record?: any): React.ReactNode {
	if (!value) return null;
	const date = new Date(value);
	if (isNaN(date.getTime())) return null;
	return React.createElement(
		'span',
		{ title: formatDate(date).iso },
		formatDate(date).natural
	);
}

export function renderDateRelative(value: any, record?: any): React.ReactNode {
	if (!value) return null;
	const date = new Date(value);
	if (isNaN(date.getTime())) return null;
	return React.createElement(
		'span',
		{ title: formatDate(date).iso },
		formatDate(date).relative
	);
}

export function renderBadge(value: any, record?: any): React.ReactNode {
	if (!value) return null;
	// Outline variant forces a uniform light-gray look regardless of the badge's semantic color
	return React.createElement(
		Badge,
		{ variant: "outline" },
		String(value)
	);
}

export function renderBoolean(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined) return null;
	return value ? '✓' : '✗';
}

export function renderNumber(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined) return null;
	return String(value);
}

/** Four decimals, because two would render most per-user totals as $0.00. Tooltip has the full value. */
export function renderCurrencyUsd(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined || value === '') return null;
	const amount = Number(value);
	if (!Number.isFinite(amount)) return null;
	return React.createElement('span', { title: `$${amount}` }, `$${amount.toFixed(4)}`);
}

export function renderTextMultiline(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined) return null;
	return React.createElement(
		'span',
		{ className: 'break-words whitespace-pre-wrap' },
		String(value)
	);
}

export function renderTextSingleLine(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined) return null;
	return React.createElement(
		'span',
		{ className: 'truncate' },
		String(value)
	);
}

export function renderText(value: any, record?: any): React.ReactNode {
	return renderTextMultiline(value, record);
}

export function getTableCellDisplayClassName(renderType?: string, fieldType?: string): string {
	const isTextField = fieldType === 'text' || fieldType === 'ai prompt';
	if (isTextField && renderType === 'text-single-line') {
		return 'block min-w-0 truncate';
	}
	if (isTextField) {
		return 'break-words whitespace-pre-wrap';
	}
	// Block, not flex: text-overflow ellipsis does not apply to flex containers.
	return 'block min-w-0 truncate';
}

/** SystemUserCell looks up profile data from workspaceMembers in Zustand state. */
export function renderSystemUser(value: any, _record?: any): React.ReactNode {
	if (!value || value === 'unassigned') {
		return React.createElement(
			'span',
			{ className: 'text-muted-foreground italic' },
			'Unassigned'
		);
	}

	// Require here to avoid circular import at module load time.
	const { SystemUserCell } = require('@/components/SystemUserCell');
	return React.createElement(SystemUserCell, { systemUserId: String(value) });
}

/**
 * Team-member-style cell (avatar + name) for admin-agent operator threads. Unlike 'systemUser'
 * it never consults the viewer's workspace member list — operators are not members of the
 * internal workspace — so name/email/avatar come from the row's own materialized fields
 * (docs/impl/104), with the auth uid in `value` still driving the deterministic avatar color.
 */
export function renderOperator(value: any, record?: any): React.ReactNode {
	if (!value) return null;
	const data = record?.data && typeof record.data === 'object' ? record.data : {};
	const pick = (key: string): string | undefined => {
		const v = record?.[key] ?? data[key];
		return typeof v === 'string' && v.trim() !== '' ? v : undefined;
	};
	const name = pick('name');
	const email = pick('email');
	const avatarUrl = pick('operatorAvatarUrl');
	const label = name || email || String(value).slice(0, 8);

	// Require here to avoid circular import at module load time.
	const { SystemUserAvatar } = require('@/components/SystemUserAvatar');
	return React.createElement(
		'span',
		{ className: 'inline-flex items-center gap-2 max-w-full', title: email || label },
		React.createElement(SystemUserAvatar, {
			systemUserId: String(value),
			size: 'sm',
			avatarUrl,
			displayName: label,
		}),
		React.createElement('span', { className: 'truncate' }, label)
	);
}

export function renderAgentVersion(value: any, record?: any): React.ReactNode {
	const versionId = Number(value);
	if (value === null || value === undefined || value === '' || !Number.isFinite(versionId)) return null;
	const fallbackLabel = record?.evalVersionLabel ?? record?.data?.evalVersionLabel;
	// Require here to avoid circular import at module load time.
	const { AgentVersionCell } = require('@/components/AgentVersionCell');
	return React.createElement(AgentVersionCell, { versionId, fallbackLabel });
}

const EVAL_STATUS_CLASSES: Record<string, string> = {
	pass: 'border-success/40 bg-success/10 text-success',
	fail: 'border-destructive/40 bg-destructive/10 text-destructive',
	error: 'border-amber-500/40 bg-amber-500/10 text-amber-600',
	running: 'text-muted-foreground',
};

/** Pass and fail as green and red badges; `true`/`false` (evalPass) map to pass/fail. */
export function renderEvalStatus(value: any, record?: any): React.ReactNode {
	if (value === null || value === undefined || value === '') return null;
	const status = value === true ? 'pass' : value === false ? 'fail' : String(value);
	return React.createElement(
		Badge,
		{ variant: 'outline', className: EVAL_STATUS_CLASSES[status] ?? '' },
		status
	);
}

/** Ignores the stored value: the cell reads the selected version's `eval_run` users. */
function renderEvalSelectedVersion(show: 'status' | 'response') {
	return (_value: any, record?: any): React.ReactNode => {
		const scenarioId = Number(record?.id);
		if (!Number.isFinite(scenarioId)) return null;
		// Require here to avoid circular import at module load time.
		const { EvalSelectedVersionCell } = require('@/components/EvalSelectedVersionCell');
		return React.createElement(EvalSelectedVersionCell, { scenarioId, show });
	};
}

export const RENDER_FUNCTION_MAP: Record<string, (value: any, record?: any) => React.ReactNode> = {
	'agentVersion': renderAgentVersion,
	'evalStatus': renderEvalStatus,
	'evalPassForSelectedVersion': renderEvalSelectedVersion('status'),
	'evalResponseForSelectedVersion': renderEvalSelectedVersion('response'),
	'date-natural': renderDateNatural,
	'date-absolute': renderDateNatural, // date-absolute is an alias for date-natural (absolute format)
	'date-relative': renderDateRelative,
	'badge': renderBadge,
	'boolean': renderBoolean,
	'number': renderNumber,
	'currency-usd': renderCurrencyUsd,
	'text': renderTextMultiline,
	'text-multiline': renderTextMultiline,
	'text-single-line': renderTextSingleLine,
	'systemUser': renderSystemUser,
	'operator': renderOperator
};

export function resolveRenderFunction(renderType?: string): (value: any, record?: any) => React.ReactNode {
	if (!renderType) return renderText;
	return RENDER_FUNCTION_MAP[renderType] || renderText;
}
